import { useEffect, useState } from 'react'
import { api, type WarenkorbBild } from '../api'
import { anzahl, prozent } from '../format'
import { Karte } from '../komponenten/Karte'
import { Kennzahl } from '../komponenten/Kennzahl'

/* Was auf demselben Tisch zusammen bestellt wurde.
 *
 * Der Korb ist die Tischbelegung, nicht der Bon: Ein Tisch bestellt
 * mehrmals, und über Bons gerechnet wäre „Bier zuerst, Steak später"
 * nie ein Paar.
 *
 * Die Grundlage steht vor den Paaren und nicht hinter ihnen. Eine
 * Paarliste ohne die Angabe, wie viele Paare überhaupt prüfbar sind
 * und wie viele über dem Zufall liegen, ist eine Rangliste der
 * Verkaufsschlager — und wird als Erkenntnis gelesen, die sie nicht
 * ist.
 */

const EBENEN = [
  { wert: 'gruppe' as const, name: 'Warengruppen' },
  { wert: 'artikel' as const, name: 'Artikel' },
]

export function Warenkorb({ von, bis, betriebe }: {
  von: string; bis: string; betriebe: string[]
}) {
  const [ebene, setEbene] = useState<'gruppe' | 'artikel'>('gruppe')
  const [d, setD] = useState<WarenkorbBild | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)

  useEffect(() => {
    let weg = false
    setLaedt(true); setFehler(null)
    api.warenkorb(von, bis, betriebe, ebene)
      .then((x) => { if (!weg) setD(x) })
      .catch((e) => { if (!weg) setFehler(e.message) })
      .finally(() => { if (!weg) setLaedt(false) })
    return () => { weg = true }
  }, [von, bis, betriebe.join(','), ebene])

  const schalter = (
    <div className="knopfleiste" role="group" aria-label="Ebene">
      {EBENEN.map((e) => (
        <button key={e.wert} aria-pressed={ebene === e.wert}
                onClick={() => setEbene(e.wert)}>{e.name}</button>
      ))}
    </div>
  )

  if (fehler) return <div className="fehler">{fehler}</div>
  if (laedt && !d) return <div className="laedt">Wird geladen …</div>
  if (!d) return null

  const sache = ebene === 'gruppe' ? 'Warengruppen' : 'Artikel'

  if (!d.koerbe) {
    return (
      <>
        <div style={{ marginBottom: 14 }}>{schalter}</div>
        <div className="karte"><div className="leer" style={{
          maxWidth: '62ch', margin: '0 auto', lineHeight: 1.7,
        }}>
          <b style={{ color: 'var(--schrift)' }}>Keine Tischbelegungen im Zeitraum.</b>
          <div style={{ marginTop: 10 }}>
            Der Warenkorb wird beim Einlesen des Kassenarchivs
            zusammengestellt. Für Tage, die vor dieser Auswertung
            eingelesen wurden, entsteht er beim nächsten Import.
          </div>
        </div></div>
      </>
    )
  }

  // Die Anhängequote: Nenner sind die Körbe mit einer Speise. Geteilt
  // wird hier, nicht im Server — Zähler und Nenner kommen getrennt.
  const anhang = [...d.sachen]
    .sort((a, b) => b.mit_speise - a.mit_speise)
    .slice(0, 12)
  const maxAnhang = Math.max(...anhang.map((z) => z.mit_speise), 1)
  const maxZusammen = Math.max(...d.zeilen.map((z) => z.zusammen), 1)

  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        marginBottom: 14, flexWrap: 'wrap',
      }}>
        {schalter}
        <span className="dezent" style={{ fontSize: 12 }}>
          {ebene === 'gruppe'
            ? 'Warengruppen tragen früher als Artikel: dieselbe Zahl Körbe verteilt sich auf weniger Sachen.'
            : `Artikel, die in mindestens ${anzahl(d.mindestbasis)} Körben lagen.`}
        </span>
      </div>

      <div className="gitter g-4" style={{ marginBottom: 14 }}>
        <Kennzahl etikett="Tischbelegungen" wert={anzahl(d.koerbe)}
                  fuss={`${anzahl(d.speisenkoerbe)} davon mit einer Speise`} />
        <Kennzahl etikett={`${sache} je Besuch`}
                  wert={d.je_korb !== null ? d.je_korb.toLocaleString('de-DE') : '–'}
                  fuss={`${anzahl(d.sachen.length)} kommen oft genug vor, um zu zählen`} />
        <Kennzahl etikett="Prüfbare Paare" wert={anzahl(d.pruefbar)}
                  fuss={`von ${anzahl(d.paare)} überhaupt vorgekommenen`} />
        <Kennzahl etikett="Über dem Zufall" wert={anzahl(d.auffaellig)}
                  fuss={d.schranke
                    ? `ab ${d.schranke.toLocaleString('de-DE')} Standardabweichungen`
                    : 'keine Basis zum Prüfen'} />
      </div>

      <div className="gitter g-2-1" style={{ marginBottom: 14 }}>
        <Karte titel="Was zusammen auf dem Tisch lag"
               neben={`häufigste Paare · ${anzahl(d.zeilen.length)} von ${anzahl(d.paare)}`}>
          {d.zeilen.length ? (
            <div style={{ maxHeight: 560, overflowY: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Paar</th>
                    <th className="r">zusammen</th>
                    <th className="r">erwartet</th>
                    <th className="r">Faktor</th>
                    <th style={{ width: 86 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {d.zeilen.map((z) => (
                    <tr key={`${z.a}|${z.b}`}>
                      <td>
                        {z.name_a} <span className="dezent">+</span> {z.name_b}
                        {z.auffaellig && (
                          <span className="merker" style={{ marginLeft: 7 }}>
                            {z.auffaellig === 'MEHR'
                              ? 'öfter als zufällig'
                              : 'seltener als zufällig'}
                          </span>
                        )}
                      </td>
                      <td className="r zahl">{anzahl(z.zusammen)}</td>
                      <td className="r zahl dezent">
                        {z.erwartet === null ? '–'
                          : z.erwartet.toLocaleString('de-DE',
                              { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                      </td>
                      <td className="r zahl">
                        {z.lift === null ? '–'
                          : z.lift.toLocaleString('de-DE',
                              { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td>
                        <div className="zeilenbalken-spur">
                          <div className="zeilenbalken"
                               style={{ width: `${Math.max(2, (z.zusammen / maxZusammen) * 100)}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="leer">
              Kein Paar kam oft genug vor, um gezählt zu werden.
            </div>
          )}

          <p className="dezent" style={{
            fontSize: 11, marginTop: 12, marginBottom: 0, lineHeight: 1.6,
            maxWidth: '84ch',
          }}>
            <b>Erwartet</b> ist, wie oft beide zusammen vorkämen, wenn sie
            nichts miteinander zu tun hätten. Der <b>Faktor</b> ist das
            Verhältnis dazu. Gekennzeichnet wird ein Paar erst, wenn der
            Abstand auch bei {anzahl(d.pruefbar)} geprüften Paaren nicht
            mehr durch Zufall zu erklären ist — wer tausend Münzen wirft,
            findet immer eine, die zehnmal Kopf zeigt.
          </p>
        </Karte>

        <div style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
          <Karte titel="Was zur Speise dazukommt" neben="Anhängequote">
            {d.speisenkoerbe ? (
              <table>
                <tbody>
                  {anhang.map((z) => (
                    <tr key={z.schluessel}>
                      <td>{z.name}</td>
                      <td className="r zahl">
                        {prozent((z.mit_speise / d.speisenkoerbe) * 100, 0)}
                      </td>
                      <td style={{ width: 64 }}>
                        <div className="zeilenbalken-spur">
                          <div className="zeilenbalken"
                               style={{ width: `${Math.max(2, (z.mit_speise / maxAnhang) * 100)}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="leer">Keine Speisen im Zeitraum.</div>
            )}
            <p className="dezent" style={{
              fontSize: 11, marginTop: 12, marginBottom: 0, lineHeight: 1.6,
            }}>
              Gerechnet auf die {anzahl(d.speisenkoerbe)} Tischbelegungen mit
              einer Speise. Diese Quote trägt schon an einem einzigen Tag:
              Ihr Nenner ist groß, nicht ein einzelnes Paar.
            </p>
          </Karte>

          <Karte titel="Was hier nicht steht">
            <ul style={{
              margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.65,
              color: 'var(--schrift-2)',
            }}>
              <li>
                <b>In welcher Reihenfolge</b> bestellt wurde. Eine Position
                trägt keinen eigenen Zeitstempel, nur ihr Bestellvorgang —
                und in dem liegen bis zu 69 Positionen zugleich.
              </li>
              <li style={{ marginTop: 7 }}>
                <b>Wer am Tisch was hatte.</b> Vier Steaks und vier
                Getränke auf einem Vierertisch sind acht Korbinhalte ohne
                Zuordnung zu Personen.
              </li>
              <li style={{ marginTop: 7 }}>
                <b>Ob derselbe Gast wiederkommt.</b> Die Kasse führt keine
                Gästekennung.
              </li>
            </ul>
          </Karte>
        </div>
      </div>

      <Karte titel={`Wie oft jede ${ebene === 'gruppe' ? 'Warengruppe' : 'Artikel'} im Korb lag`}
             neben={`mindestens ${anzahl(d.mindestbasis)} Körbe`}>
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>{ebene === 'gruppe' ? 'Warengruppe' : 'Artikel'}</th>
                <th className="r">Tischbelegungen</th>
                <th className="r">Anteil</th>
                <th className="r">mit Speise</th>
              </tr>
            </thead>
            <tbody>
              {d.sachen.map((z) => (
                <tr key={z.schluessel}>
                  <td>{z.name}</td>
                  <td className="r zahl">{anzahl(z.koerbe)}</td>
                  <td className="r zahl dezent">
                    {prozent((z.koerbe / d.koerbe) * 100, 0)}
                  </td>
                  <td className="r zahl dezent">{anzahl(z.mit_speise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Karte>
    </>
  )
}
